import {
  BarsOutlined,
  EyeInvisibleOutlined,
  LogoutOutlined,
  RollbackOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  SkinOutlined,
} from '@ant-design/icons';
import { Badge, ConfigProvider, Tabs, TabsProps } from 'antd';
import React, { useState } from 'react';
import { StatBox } from '../../Analytics/EmailAnalytics/EmailSummaryStats';
import styles from './SignalStats.module.css';

const NoData = () => (
  <div className={styles.noData}>
    <div className={styles.badge}>
      <ConfigProvider
        theme={{
          components: {
            Badge: {
              dotSize: 8,
            },
          },
        }}
      >
        <Badge color="#1890FF" />
      </ConfigProvider>
    </div>
    <div>Will appear here</div>
  </div>
);

export const SignalStats = ({
  data,
  activeTab,
  setActiveTab,
  setContactOrigin,
  configuration,
}: {
  data: {
    activeOnSite: number | null;
    productView: number | null;
    collectionView: number | null;
    addToCart: number | null;
    checkoutStarted: number | null;
    returnVisit: number | null;
    trackingGapFilled: number | null;
    websiteVisit: number | null;
  };
  activeTab: string;
  setActiveTab: (activeTab: 'all' | 'new' | 'existing') => void;
  setContactOrigin: (val: 'Existing' | 'New' | null) => void;
  configuration?: {
    integrations?: string[];
    mode?: string;
  };
}) => {
  const [tabCounters] = useState<Record<string, number | null>>({
    new: null,
    existing: null,
    all: null,
  });

  const handleTabChange = (key: string) => {
    setActiveTab(key as 'all' | 'new' | 'existing');
    if (key === 'all') setContactOrigin(null);
    else if (key === 'new') setContactOrigin('New');
    else if (key === 'existing') setContactOrigin('Existing');
  };

  const hasKlaviyo = configuration?.integrations?.includes('klaviyo');
  const isManual = configuration?.mode === 'manual';

  const statBoxConfigs = [
    // Show "Website visit" as the first option for klaviyoManual mode
    ...(isManual
      ? [
          {
            label: 'Website visit',
            icon: <SearchOutlined className={styles.icon} />,
            stat:
              data?.websiteVisit === null ? (
                <NoData />
              ) : (
                (data?.websiteVisit || 0).toLocaleString()
              ),
          },
        ]
      : []),
    // Only show "Active On Site" if Klaviyo is enabled
    ...(hasKlaviyo
      ? [
          {
            label: 'Active On Site',
            icon: <SearchOutlined className={styles.icon} />,
            stat:
              data?.activeOnSite === null ? (
                <NoData />
              ) : (
                (data?.activeOnSite || 0).toLocaleString()
              ),
          },
        ]
      : []),
    {
      label: 'Product View',
      icon: <SkinOutlined className={styles.icon} />,
      stat:
        data?.productView === null ? (
          <NoData />
        ) : (
          (data?.productView || 0).toLocaleString()
        ),
    },
    {
      label: 'Collection View',
      icon: <BarsOutlined className={styles.icon} />,
      stat:
        data?.collectionView === null ? (
          <NoData />
        ) : (
          (data?.collectionView || 0).toLocaleString()
        ),
    },
    {
      label: 'Added to Cart',
      icon: <ShoppingCartOutlined className={styles.icon} />,
      stat:
        data?.addToCart === null ? (
          <NoData />
        ) : (
          (data?.addToCart || 0).toLocaleString()
        ),
    },
    {
      label: 'Checkout Started',
      icon: <LogoutOutlined className={styles.icon} />,
      stat:
        data?.checkoutStarted === null ? (
          <NoData />
        ) : (
          (data?.checkoutStarted || 0).toLocaleString()
        ),
    },
    {
      label: 'Return Visit',
      icon: <RollbackOutlined className={styles.icon} />,
      stat:
        data?.returnVisit === null ? (
          <NoData />
        ) : (
          (data?.returnVisit || 0).toLocaleString()
        ),
    },
    // Only show "Tracking Gap Klaviyo" if Klaviyo is enabled
    ...(hasKlaviyo
      ? [
          {
            label: 'Tracking Gap Klaviyo',
            icon: <EyeInvisibleOutlined className={styles.icon} />,
            stat:
              data?.trackingGapFilled === null ? (
                <NoData />
              ) : (
                (data?.trackingGapFilled || 0).toLocaleString()
              ),
          },
        ]
      : []),
  ];

  const tabItems: TabsProps['items'] = [
    {
      key: 'all',
      label: (
        // Note: temporary commenting this stats out till BE supports it
        // <div>All Contacts ({tabCounters.all?.toLocaleString() || 0}) </div>
        <div>All Contacts</div>
      ),
    },
    {
      key: 'new',
      label: (
        // <div>New Contacts ({tabCounters.new?.toLocaleString() || 0}) </div>
        <div>New Contacts</div>
      ),
    },
    {
      key: 'existing',
      label: (
        // <div>
        //   Existing Contacts ({tabCounters.existing?.toLocaleString() || 0}){' '}
        // </div>
        <div>Existing Contacts</div>
      ),
    },
  ];

  return (
    <div className={styles.statsContainer}>
      <div className={styles.title}>Analytics</div>
      <Tabs
        className={styles.tabs}
        activeKey={activeTab}
        items={tabItems}
        onChange={handleTabChange}
      />
      <div className={styles.statsRow}>
        {statBoxConfigs.map(config => (
          <StatBox
            key={config.label}
            className={styles.box}
            label={config.label}
            icon={config.icon}
            stat={config.stat}
          />
        ))}
      </div>
    </div>
  );
};
